import { useCallback, useEffect, useMemo, useState } from 'react';
import type { StudyListParams } from '@/models/study';
import type {
  ReportStatusFilter,
  PriorityFilter,
} from '../WorklistFilterRail/WorklistFilterRail';

const STORAGE_KEY = 'worklist-filters-v2';

interface StoredFilters {
  assignedToMe: boolean;
  reportStatus: ReportStatusFilter[];
  priority: PriorityFilter[];
}

const DEFAULTS: StoredFilters = {
  assignedToMe: true,
  reportStatus: ['DRAFT', 'PRELIMINARY'],
  priority: [],
};

function loadFilters(): StoredFilters {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as StoredFilters;
  } catch {
    // ignore
  }
  return DEFAULTS;
}

function saveFilters(filters: StoredFilters) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
}

export function useWorklistFilters() {
  const [assignedToMe, setAssignedToMe] = useState(
    () => loadFilters().assignedToMe
  );
  const [reportStatusFilters, setReportStatusFilters] = useState<
    ReportStatusFilter[]
  >(() => loadFilters().reportStatus);
  const [priorityFilters, setPriorityFilters] = useState<PriorityFilter[]>(
    () => loadFilters().priority
  );
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedName, setDebouncedName] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [ordering, setOrdering] = useState<string | undefined>();

  // Persist filter selections
  useEffect(() => {
    saveFilters({
      assignedToMe,
      reportStatus: reportStatusFilters,
      priority: priorityFilters,
    });
  }, [assignedToMe, reportStatusFilters, priorityFilters]);

  // Debounced search
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedName(searchText.trim() || undefined);
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchText]);

  // Reset page when filters change
  const handleAssignedChange = useCallback((v: boolean) => {
    setAssignedToMe(v);
    setPage(1);
  }, []);

  const handleReportStatusChange = useCallback((v: ReportStatusFilter[]) => {
    setReportStatusFilters(v);
    setPage(1);
  }, []);

  const handlePriorityChange = useCallback((v: PriorityFilter[]) => {
    setPriorityFilters(v);
    setPage(1);
  }, []);

  const handleTagChange = useCallback((v: string[]) => {
    setTagFilters(v);
    setPage(1);
  }, []);

  const hasActiveFilters = !!(
    assignedToMe ||
    reportStatusFilters.length > 0 ||
    priorityFilters.length > 0 ||
    tagFilters.length > 0 ||
    debouncedName
  );

  const clearFilters = useCallback(() => {
    setAssignedToMe(false);
    setReportStatusFilters([]);
    setPriorityFilters([]);
    setTagFilters([]);
    setSearchText('');
    setDebouncedName(undefined);
    setPage(1);
  }, []);

  // Build API params
  const params: StudyListParams = useMemo(
    () => ({
      page,
      size: pageSize,
      ordering,
      is_assigned: assignedToMe || undefined,
      report_status:
        reportStatusFilters.length > 0 ? reportStatusFilters : undefined,
      priority: priorityFilters.length > 0 ? priorityFilters : undefined,
      tags: tagFilters.length > 0 ? tagFilters : undefined,
      patient_name: debouncedName,
    }),
    [
      page,
      pageSize,
      ordering,
      assignedToMe,
      reportStatusFilters,
      priorityFilters,
      tagFilters,
      debouncedName,
    ]
  );

  return {
    params,
    page,
    pageSize,
    setPage,
    setPageSize,
    setOrdering,
    assignedToMe,
    setAssignedToMe: handleAssignedChange,
    reportStatusFilters,
    setReportStatusFilters: handleReportStatusChange,
    priorityFilters,
    setPriorityFilters: handlePriorityChange,
    tagFilters,
    setTagFilters: handleTagChange,
    searchText,
    setSearchText,
    hasActiveFilters,
    clearFilters,
  };
}
